/**
 * Type definitions and interfaces for MindMap state
 * Provides structure and validation for mind map data
 */

/**
 * @typedef {Object} Position
 * @property {number} x - X coordinate
 * @property {number} y - Y coordinate
 */

/**
 * @typedef {Object} NodeData
 * @property {string} id - Unique identifier
 * @property {string} text - Node text content
 * @property {Position} position - Node position
 * @property {string} color - Node background gradient
 * @property {boolean} isRoot - Whether this is the root node
 * @property {boolean} [animate] - Whether to animate position changes
 * @property {boolean} [autoFocus] - Whether to auto-focus when created
 * @property {Object} [collapsedChildren] - Collapsed children data
 * @property {string} [emoji] - Optional emoji displayed on the node
 * @property {string} [info] - Optional collaborative Markdown context for the node
 * @property {string|null} [userId] - ID of the user who originally created the node
 * @property {string|null} [lastEditedBy] - ID of the user who last edited the node
 * @property {string|null} [creatorName] - Display name of the user who originally created the node
 */

/**
 * @typedef {Object} RelationshipData
 * @property {string} parentId - Parent node ID
 * @property {string} childId - Child node ID
 */

/**
 * @typedef {Object} MindMapStateData
 * @property {NodeData[]} nodes - All nodes in the mind map
 * @property {RelationshipData[]} relationships - Parent-child relationships
 * @property {string|null} root - Root node ID
 * @property {string|null} selectedId - Currently selected node ID
 */

/**
 * Represents the complete state of a mind map
 * Provides validation and helper methods for state management
 */
export class MindMapState {
  /**
   * Create a new MindMapState
   * @param {MindMapStateData} data - Initial state data
   */
  constructor(data = {}) {
    this.nodes = data.nodes || [];
    this.relationships = data.relationships || [];
    this.root = data.root || null;
    this.selectedId = data.selectedId || null;
  }

  /**
   * Validate the current state
   * @returns {{isValid: boolean, errors: string[]}} Validation result
   */
  validate() {
    const errors = [];

    // Check if root exists and is valid
    if (this.root && !this.nodes.find(n => n.id === this.root)) {
      errors.push('Root node ID does not exist in nodes array');
    }

    // Check if selected node exists
    if (this.selectedId && !this.nodes.find(n => n.id === this.selectedId)) {
      errors.push('Selected node ID does not exist in nodes array');
    }

    // Check for duplicate node IDs
    const nodeIds = this.nodes.map(n => n.id);
    const uniqueIds = new Set(nodeIds);
    if (nodeIds.length !== uniqueIds.size) {
      errors.push('Duplicate node IDs found');
    }

    // Check relationship validity
    this.relationships.forEach((rel, index) => {
      if (!this.nodes.find(n => n.id === rel.parentId)) {
        errors.push(`Relationship ${index}: Parent ID ${rel.parentId} not found`);
      }
      if (!this.nodes.find(n => n.id === rel.childId)) {
        errors.push(`Relationship ${index}: Child ID ${rel.childId} not found`);
      }
    });

    // Check for circular relationships
    if (this.hasCircularRelationships()) {
      errors.push('Circular relationships detected');
    }

    // Check root node properties
    const rootNode = this.nodes.find(n => n.id === this.root);
    if (rootNode && !rootNode.isRoot) {
      errors.push('Root node must have isRoot = true');
    }

    // Check for multiple root nodes
    const rootNodes = this.nodes.filter(n => n.isRoot);
    if (rootNodes.length > 1) {
      errors.push('Multiple nodes marked as root');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Check for circular relationships in the graph
   * @returns {boolean} True if circular relationships exist
   */
  hasCircularRelationships() {
    const visited = new Set();
    const recursionStack = new Set();

    const hasCycle = (nodeId) => {
      if (recursionStack.has(nodeId)) return true;
      if (visited.has(nodeId)) return false;

      visited.add(nodeId);
      recursionStack.add(nodeId);

      const children = this.relationships
        .filter(r => r.parentId === nodeId)
        .map(r => r.childId);

      for (const childId of children) {
        if (hasCycle(childId)) return true;
      }

      recursionStack.delete(nodeId);
      return false;
    };

    for (const node of this.nodes) {
      if (!visited.has(node.id)) {
        if (hasCycle(node.id)) return true;
      }
    }

    return false;
  }

  /**
   * Get all descendants of a node
   * @param {string} nodeId - Parent node ID
   * @returns {string[]} Array of descendant node IDs
   */
  getDescendants(nodeId) {
    const descendants = [];
    const stack = [nodeId];

    while (stack.length > 0) {
      const currentId = stack.pop();
      const children = this.relationships
        .filter(r => r.parentId === currentId)
        .map(r => r.childId);

      for (const childId of children) {
        if (!descendants.includes(childId)) {
          descendants.push(childId);
          stack.push(childId);
        }
      }
    }

    return descendants;
  }

  /**
   * Get direct children of a node
   * @param {string} nodeId - Parent node ID
   * @returns {string[]} Array of child node IDs
   */
  getChildren(nodeId) {
    return this.relationships
      .filter(r => r.parentId === nodeId)
      .map(r => r.childId);
  }

  /**
   * Get parent of a node
   * @param {string} nodeId - Child node ID
   * @returns {string|null} Parent node ID or null
   */
  getParent(nodeId) {
    const relationship = this.relationships.find(r => r.childId === nodeId);
    return relationship ? relationship.parentId : null;
  }

  /**
   * Get siblings of a node
   * @param {string} nodeId - Node ID
   * @returns {string[]} Array of sibling node IDs
   */
  getSiblings(nodeId) {
    const parent = this.getParent(nodeId);
    if (!parent) return [];

    return this.getChildren(parent).filter(id => id !== nodeId);
  }

  /**
   * Get depth/level of a node (root = 0)
   * @param {string} nodeId - Node ID
   * @returns {number} Depth level
   */
  getNodeDepth(nodeId) {
    if (nodeId === this.root) return 0;

    let depth = 0;
    let currentId = nodeId;

    while (currentId !== this.root) {
      const parent = this.getParent(currentId);
      if (!parent) return -1; // Orphaned node
      currentId = parent;
      depth++;
    }

    return depth;
  }

  /**
   * Get all nodes at a specific depth level
   * @param {number} depth - Depth level
   * @returns {NodeData[]} Nodes at the specified depth
   */
  getNodesAtDepth(depth) {
    return this.nodes.filter(node => this.getNodeDepth(node.id) === depth);
  }

  /**
   * Clone the state
   * @returns {MindMapState} New MindMapState instance
   */
  clone() {
    return new MindMapState({
      nodes: JSON.parse(JSON.stringify(this.nodes)),
      relationships: JSON.parse(JSON.stringify(this.relationships)),
      root: this.root,
      selectedId: this.selectedId
    });
  }

  /**
   * Serialize to plain object
   * @returns {MindMapStateData} Serialized state
   */
  toJSON() {
    return {
      nodes: this.nodes,
      relationships: this.relationships,
      root: this.root,
      selectedId: this.selectedId
    };
  }

  /**
   * Create MindMapState from serialized data
   * @param {MindMapStateData} data - Serialized state data
   * @returns {MindMapState} New MindMapState instance
   */
  static fromJSON(data) {
    return new MindMapState(data);
  }

  /**
   * Get statistics about the mind map
   * @returns {Object} Statistics object
   */
  getStatistics() {
    const maxDepth = Math.max(...this.nodes.map(n => this.getNodeDepth(n.id)));
    const nodesByDepth = {};

    for (let i = 0; i <= maxDepth; i++) {
      nodesByDepth[i] = this.getNodesAtDepth(i).length;
    }

    return {
      totalNodes: this.nodes.length,
      totalRelationships: this.relationships.length,
      maxDepth,
      nodesByDepth,
      hasSelection: !!this.selectedId,
      isValid: this.validate().isValid
    };
  }
}

// Export type definitions for JSDoc
export const NodeDataType = {};
export const RelationshipDataType = {};
export const MindMapStateDataType = {};
